import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import type { PredictionLeg } from "../lib/composePayoff";
import type { SelectedPerp, SelectedPred } from "../types";
import { PredictionSimulateForm } from "./prediction-simulate-form";

function Chevron({ open, color }: { open: boolean; color: string }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden
      style={{
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 180ms ease",
        color,
      }}
    >
      <path
        d="M2 4.5 L6 8.5 L10 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const PRED_COLOR = "#60a5fa";

/**
 * Deterministic hue from an arbitrary string. Used to pick a "main colour"
 * tint for a prediction market's card when we don't have the actual image's
 * dominant colour.
 */
function hueFor(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function cardGradient(seed: string): {
  gradient: string;
  accent: string;
  tint: string;
} {
  const hue = hueFor(seed);
  const accent = `hsl(${hue}, 72%, 64%)`;
  const tint = `hsla(${hue}, 72%, 58%, 0.22)`;
  const gradient = `
    radial-gradient(120% 120% at 0% 0%, hsla(${hue}, 72%, 58%, 0.20), transparent 55%),
    linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 100%)
  `;
  return { gradient, accent, tint };
}

const monoLabel: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 9,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--fg-meta)",
};

const tabBtn = (active: boolean): CSSProperties => ({
  flex: 1,
  padding: "8px 10px",
  background: "transparent",
  border: "none",
  borderBottom: active ? `1px solid ${PRED_COLOR}` : "1px solid transparent",
  color: active ? PRED_COLOR : "var(--fg-dim)",
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.16em",
  fontWeight: 500,
  textTransform: "uppercase",
  cursor: "pointer",
});

export type PredPanelMode = "existing" | "simulate";

function formatUsd(n: number): string {
  if (!isFinite(n) || n === 0) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

function formatEnd(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return "ENDED";
    if (days === 0) return "TODAY";
    if (days === 1) return "1D";
    if (days < 365) return `${days}D`;
    return `${(days / 365).toFixed(1)}Y`;
  } catch {
    return "—";
  }
}

function MetricChip({
  label,
  value,
  color,
  accent,
}: {
  label: string;
  value: string;
  color?: string;
  accent?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: "6px 8px",
        border: `1px solid ${accent ? `${accent}33` : "var(--line-1)"}`,
        background: "rgba(0,0,0,0.25)",
        borderRadius: 2,
        minWidth: 0,
      }}
    >
      <span style={{ ...monoLabel, fontSize: 8, letterSpacing: "0.14em" }}>
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          color: color ?? "var(--fg-hi)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function PredictionsPanel({
  selected,
  leg,
  mode,
  perp,
  perpSymbol,
  onModeChange,
  onLegChange,
  onSimulateInit,
  onExplore,
}: {
  selected: SelectedPred | null;
  leg: PredictionLeg | null;
  mode: PredPanelMode;
  perp: SelectedPerp | null;
  perpSymbol: string | undefined;
  onModeChange: (mode: PredPanelMode) => void;
  onLegChange: (leg: PredictionLeg) => void;
  onSimulateInit: () => void;
  onExplore: () => void;
}) {
  if (!selected) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 14,
          padding: 24,
        }}
      >
        <div style={{ ...monoLabel, color: "var(--fg-dim)", textAlign: "center" }}>
          NO PREDICTION SELECTED
        </div>
        <button
          type="button"
          onClick={onExplore}
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontWeight: 500,
            color: PRED_COLOR,
            background: "transparent",
            border: `1px solid ${PRED_COLOR}`,
            padding: "6px 14px",
            cursor: "pointer",
            borderRadius: 2,
          }}
        >
          BROWSE MARKETS →
        </button>
      </div>
    );
  }

  const yesPct =
    selected.yesPrice !== undefined ? (selected.yesPrice * 100).toFixed(1) : null;
  const noPct =
    selected.noPrice !== undefined ? (selected.noPrice * 100).toFixed(1) : null;

  const { gradient, accent, tint } = cardGradient(
    selected.category ?? selected.id ?? selected.question,
  );

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* HERO — prominent gradient card tinted with the market's colour */}
      <div
        style={{
          position: "relative",
          margin: 10,
          padding: "12px 14px 14px",
          minHeight: 116,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          justifyContent: "space-between",
          borderRadius: 3,
          background: gradient,
          border: `1px solid ${tint}`,
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.02), 0 1px 0 rgba(0,0,0,0.3)`,
          overflow: "hidden",
        }}
      >
        {/* Polymarket provider watermark */}
        <img
          src="/polymarket/logos/icon-blue.svg"
          alt=""
          aria-hidden
          style={{
            position: "absolute",
            right: 10,
            bottom: 10,
            height: 14,
            opacity: 0.5,
            pointerEvents: "none",
          }}
        />
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          {selected.image ? (
            <img
              src={selected.image}
              alt=""
              style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                objectFit: "cover",
                border: `1px solid ${tint}`,
                borderRadius: 2,
                boxShadow: `0 0 0 2px rgba(0,0,0,0.2)`,
              }}
            />
          ) : (
            <img
              src="/polymarket/logos/icon-blue.svg"
              alt=""
              style={{ width: 28, height: 28, flexShrink: 0, opacity: 0.9 }}
            />
          )}
          <div
            style={{
              fontFamily: "var(--f-sans)",
              fontSize: 12,
              color: "var(--fg-max)",
              lineHeight: 1.3,
              minWidth: 0,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
            title={selected.question}
          >
            {selected.question}
          </div>
        </div>

        {/* METRIC STRIP */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4 }}>
          <MetricChip
            label="YES / NO"
            value={`${yesPct ?? "—"}¢ / ${noPct ?? "—"}¢`}
            color="var(--fg-hi)"
          />
          <MetricChip label="24H VOL" value={formatUsd(selected.volume24hr)} />
          <MetricChip label="ENDS" value={formatEnd(selected.endDate)} accent={accent} />
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--line-2)" }}>
        <button
          type="button"
          style={tabBtn(mode === "existing")}
          onClick={() => onModeChange("existing")}
        >
          EXISTING
        </button>
        <button
          type="button"
          style={tabBtn(mode === "simulate")}
          onClick={() => {
            onModeChange("simulate");
            if (!leg) onSimulateInit();
          }}
        >
          SIMULATE
        </button>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {mode === "existing" ? (
          <div
            style={{
              minHeight: 160,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              padding: 24,
              textAlign: "center",
            }}
          >
            <div style={{ ...monoLabel, color: "var(--fg-dim)" }}>
              NO OPEN POSITIONS ON THIS MARKET
            </div>
            <button
              type="button"
              onClick={() => {
                onModeChange("simulate");
                if (!leg) onSimulateInit();
              }}
              style={{
                fontFamily: "var(--f-mono)",
                fontSize: 10,
                letterSpacing: "0.16em",
                fontWeight: 500,
                textTransform: "uppercase",
                color: PRED_COLOR,
                background: `${PRED_COLOR}10`,
                border: `1px solid ${PRED_COLOR}`,
                padding: "8px 18px",
                cursor: "pointer",
                borderRadius: 2,
              }}
            >
              SIMULATE POSITION →
            </button>
          </div>
        ) : leg ? (
          <SimulateExtension
            leg={leg}
            market={selected}
            perp={perp}
            perpSymbol={perpSymbol}
            onChange={onLegChange}
          />
        ) : null}
      </div>
    </div>
  );
}

function SimulateExtension({
  leg,
  market,
  perp,
  perpSymbol,
  onChange,
}: {
  leg: PredictionLeg;
  market: SelectedPred;
  perp: SelectedPerp | null;
  perpSymbol: string | undefined;
  onChange: (next: PredictionLeg) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => setExpanded(false), [market.id]);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          background: "transparent",
          border: "none",
          borderBottom: expanded ? "1px solid var(--line-2)" : undefined,
          color: expanded ? PRED_COLOR : "var(--fg-meta)",
          fontFamily: "var(--f-mono)",
          fontSize: 10,
          letterSpacing: "0.16em",
          fontWeight: 500,
          textTransform: "uppercase",
          cursor: "pointer",
          boxShadow: `inset 2px 0 0 ${PRED_COLOR}`,
          transition: "color 160ms ease",
        }}
        aria-expanded={expanded}
      >
        <span>Simulated Position</span>
        <Chevron open={expanded} color="currentColor" />
      </button>
      {expanded && (
        <PredictionSimulateForm
          market={market}
          perp={perp}
          perpSymbol={perpSymbol}
          leg={leg}
          onChange={onChange}
        />
      )}
    </div>
  );
}
